import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common';
import { routes } from '../../lib/routes';
import type { TourName } from '../../tooltips/copy';

interface TourOption {
  name: TourName;
  label: string;
  description: string;
  route: string;
}

const TOUR_OPTIONS: TourOption[] = [
  {
    name: 'dashboard',
    label: 'Dashboard',
    description: 'Navigation overview: matches, discover, profile.',
    route: routes.home,
  },
  {
    name: 'matches',
    label: 'Matches',
    description: 'How scores, Primary/Adjacent tiers, and reasoning work.',
    route: routes.matches,
  },
  {
    name: 'match-detail',
    label: 'Match detail',
    description: 'Headline, breakdown, synergies, friction, and reaching out.',
    route: routes.matches,
  },
  {
    name: 'discover',
    label: 'Discover',
    description: 'Browsing, anonymous profiles, expressing interest.',
    route: routes.discover,
  },
  {
    name: 'chat',
    label: 'Chat',
    description: 'Conversations, safety controls.',
    route: routes.chat,
  },
];

/**
 * Settings "Replay product tour" panel. Each button navigates to the tour's
 * anchor page with `?replay=<name>`, which the <Tour> wrapper reads to
 * force-run even if the user has already completed it.
 */
const ProductTour: React.FC = () => {
  const navigate = useNavigate();

  const replay = (option: TourOption) => {
    navigate(`${option.route}?replay=${option.name}`);
  };

  return (
    <div className='max-w-2xl'>
      <h4 className='!font-medium text-heading-500 mb-2'>Replay product tour</h4>
      <p className='text-sm text-body-400 mb-6'>
        Re-watch any of the guided walkthroughs. These are the same tours that run the
        first time you visit each surface.
      </p>
      <div className='space-y-3'>
        {TOUR_OPTIONS.map(option => (
          <div
            key={option.name}
            className='flex items-center justify-between gap-4 p-4 border border-purple-25 rounded-xl'
          >
            <div className='flex-1'>
              <div className='font-medium text-heading-500'>{option.label}</div>
              <div className='text-sm text-body-400'>{option.description}</div>
            </div>
            <Button variant='outline' size='sm' onClick={() => replay(option)}>
              Replay
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductTour;
